import type { TFunction } from "i18next";
import type { ErrorTranslation } from "../error-translations";
import { ApiError } from "./api";

type ErrorKey = keyof ErrorTranslation;

const AUTHENTICATION = new Set(["account_required", "authentication_required", "invalid_session", "invalid_oauth_handoff", "oauth_handoff_used"]);
const PERMISSION = new Set(["admin_write_forbidden", "feature_not_available", "oauth_identity_in_use"]);
const NOT_FOUND = new Set(["resource_not_found", "payment_order_not_found", "subscription_not_found", "billing_customer_not_found"]);
const CONFLICT = new Set(["revision_conflict", "idempotency_conflict", "code_state_conflict", "upload_state_conflict", "payment_event_conflict", "oauth_state_used"]);
const RATE_LIMITED = new Set(["email_rate_limited", "guest_rate_limited", "ip_rate_limited", "too_many_attempts"]);
const QUOTA = new Set(["quota_exceeded"]);
const FILE_SIZE = new Set(["file_size_limit_exceeded", "subtitle_too_large"]);
const DURATION = new Set(["media_duration_limit_exceeded"]);
const FORMAT = new Set(["media_format_unsupported", "remote_media_type_unsupported", "invalid_subtitle_format", "invalid_export_format"]);
const EMAIL = new Set(["email_not_configured", "email_send_failed", "delivery_failed", "delivery_resource_missing"]);
const PAYMENT = new Set(["invalid_billing_period", "invalid_plan", "payment_provider_error", "payment_provider_not_configured", "price_not_available", "plan_not_available", "invalid_payment_event", "invalid_payment_signature"]);
const UNAVAILABLE = new Set(["service_not_configured", "media_host_unavailable", "remote_media_unavailable", "remote_media_download_failed", "youtube_import_failed", "pdf_not_configured", "pdf_render_failed", "export_store_failed", "transcription_not_ready", "data_export_not_ready", "oauth_not_configured", "oauth_provider_error", "oauth_provider_contract", "transport_error", "operation_failed", "archive_generation_failed"]);
const CODE_GROUPS: ReadonlyArray<readonly [ErrorKey, ReadonlySet<string>]> = [
  ["authentication", AUTHENTICATION], ["permission", PERMISSION], ["notFound", NOT_FOUND],
  ["conflict", CONFLICT], ["rateLimited", RATE_LIMITED], ["quota", QUOTA],
  ["fileSize", FILE_SIZE], ["duration", DURATION], ["format", FORMAT],
  ["email", EMAIL], ["payment", PAYMENT], ["unavailable", UNAVAILABLE],
];
const STATUS_KEYS: Readonly<Record<number, ErrorKey>> = {
  401: "authentication", 403: "permission", 404: "notFound", 409: "conflict",
  410: "notFound", 429: "rateLimited",
};

export function localizedError(reason: unknown, t: TFunction): string {
  return t(`errors.${localizedErrorKey(reason)}`);
}

export function localizedErrorKey(reason: unknown): ErrorKey {
  if (reason instanceof DOMException && (reason.name === "NotAllowedError" || reason.name === "SecurityError")) return "microphone";
  if (reason instanceof TypeError) return "network";
  if (!(reason instanceof ApiError)) return "generic";
  const code = reason.code;
  if (code === "invalid_code") return "invalidCode";
  if (code === "code_expired" || code === "code_used") return "expiredCode";
  const grouped = CODE_GROUPS.find(([, codes]) => codes.has(code));
  if (grouped) return grouped[0];
  if (reason.status >= 500) return "unavailable";
  if (code === "network_error" || code === "upload_failed") return "network";
  if (code.startsWith("invalid_") || code.startsWith("unsupported_")) return "invalidRequest";
  return STATUS_KEYS[reason.status] ?? "generic";
}
